import { usePetEdit } from '@front/logic'
import { AppBackground, BottomActionContainer, Button, Input, Text, ResponsiveLogo, Drop } from '@ui/components'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { Image, TouchableOpacity, View, Text as RNText } from 'react-native'
import Modal from 'react-native-modal'
import { loadSelectedPetAvatar, useAppDispatch, useAppSelector } from '@front/store'
import { ReactComponent as XSimpleIcon } from '@/assets/icons/x_simple_grey.svg'
import { ReactComponent as ChevronDownIcon } from '@/assets/icons/aena/aena_chevron_down.svg'
import {
  BodyText,
  ButtonContainer,
  ErrorText,
  Headline5,
  InputContainer,
  InputWrapper,
  ModalContainer,
  ModalHeader,
  PreviewButtonContainer,
} from '@/aena/styles'
import { useNavigate } from 'react-router-dom'
import { CamaraCard, ImageTypeSelector } from '@/components/cameracard'
import { t } from 'i18next'
import { HeaderWithBackAena } from '@/components/headerwithback'
import {
  getPetBreedByType,
  type PetBreedInterface,
  getBrachycephalicBreeds,
  countCountriesWithBreedRestriction,
  matchesAnyBreed,
  IBrachycephalicBreed,
} from '@front/apis'
import CheckBoxCustom from '@/components/customcheckbox'

interface BreedNoticesProps {
  isBrachy: boolean
  restriction: { block: number; warn: number; sampleIsos: string[] } | null
  onPressTravelDocs: () => void
}

const BreedNotices: FC<BreedNoticesProps> = ({ isBrachy, restriction, onPressTravelDocs }) => {
  const hasBlock = !!restriction && restriction.block > 0
  const hasWarn = !!restriction && restriction.warn > 0 && !hasBlock
  if (!isBrachy && !hasBlock && !hasWarn) return null
  const sampleStr = restriction ? restriction.sampleIsos.join(', ') : ''
  return (
    <View style={{ gap: 8, marginTop: 8 }}>
      {isBrachy && (
        <View
          style={{
            backgroundColor: '#fff4e5',
            borderLeftWidth: 4,
            borderLeftColor: '#f5a623',
            padding: 10,
            borderRadius: 4,
          }}
        >
          <BodyText>
            <RNText style={{ fontWeight: '600' }}>Aviso: </RNText>
            Esta raza es <RNText style={{ fontWeight: '700' }}>braquicefalica</RNText>. Para volar con Air Europa solo se
            permite Cabina con un maximo de 10 kg (con transportin). Razas superiores a ese peso no pueden volar.
          </BodyText>
        </View>
      )}
      {hasBlock && (
        <View
          style={{
            backgroundColor: '#fde8e8',
            borderLeftWidth: 4,
            borderLeftColor: '#c30000',
            padding: 10,
            borderRadius: 4,
          }}
        >
          <BodyText>
            <RNText style={{ fontWeight: '600' }}>Atencion: </RNText>
            Esta raza tiene <RNText style={{ fontWeight: '700' }}>prohibida la entrada en {restriction!.block} paises</RNText>
            {sampleStr ? ` (entre ellos: ${sampleStr})` : ''}. Consulta Documentacion viajes exterior antes de planificar
            viajes.
          </BodyText>
          <TouchableOpacity onPress={onPressTravelDocs} style={{ marginTop: 6 }}>
            <RNText style={{ color: '#007aff', textDecorationLine: 'underline' }}>
              Ver Documentacion viajes exterior
            </RNText>
          </TouchableOpacity>
        </View>
      )}
      {hasWarn && (
        <View
          style={{
            backgroundColor: '#fff9db',
            borderLeftWidth: 4,
            borderLeftColor: '#f5c518',
            padding: 10,
            borderRadius: 4,
          }}
        >
          <BodyText>
            <RNText style={{ fontWeight: '600' }}>Aviso: </RNText>
            Esta raza <RNText style={{ fontWeight: '700' }}>requiere licencia especial en {restriction!.warn} paises</RNText>
            {sampleStr ? ` (entre ellos: ${sampleStr})` : ''}. Consulta Documentacion viajes exterior antes de planificar
            viajes.
          </BodyText>
          <TouchableOpacity onPress={onPressTravelDocs} style={{ marginTop: 6 }}>
            <RNText style={{ color: '#007aff', textDecorationLine: 'underline' }}>
              Ver Documentacion viajes exterior
            </RNText>
          </TouchableOpacity>
        </View>
      )}
    </View>
  )
}



interface inputInterface {
  type: string
  label: string
  value: string
  handleChange: any
  handleBlur: any
  placeholder: string
  error?: string
}

export const PetEdit: FC = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const selectedPet = useAppSelector((state) => state.selectedPet)
  const { avatar, name } = selectedPet
  const [showModal, setShowModal] = React.useState<boolean>(false)
  const [showConfirmationModal, setShowConfirmationModal] = React.useState<boolean>(false)
  const [petBreeds, setPetBreeds] = useState<PetBreedInterface[]>([])
  const [brachycephalicBreeds, setBrachycephalicBreeds] = useState<IBrachycephalicBreed[]>([])
  const [breedRestriction, setBreedRestriction] = useState<{ block: number; warn: number; sampleIsos: string[] } | null>(null)

  const onNextPress = async () => {
    navigate(-1)
  }

  const { values, errors, handleBlur, handleChange, handleSubmit, deletePet, setFieldValue } = usePetEdit({ onNextPress })

  // Bug (a) fix: drive the Name input from local controlled state initialized ONCE
  // from the pet name. formik has enableReinitialize:true, so every redux update to
  // selectedPet reinitializes formik values; rendering the input directly off
  // values.name made react-native-web re-render/steal focus to the next field while
  // typing. Keeping a stable local state decouples the input from those reinits.
  const [nameInput, setNameInput] = useState<string>(name ?? '')
  const nameInitialized = React.useRef(false)
  useEffect(() => {
    // Initialize exactly once, when the pet name first becomes available.
    if (!nameInitialized.current && name) {
      setNameInput(name)
      nameInitialized.current = true
    }
  }, [name])

  const onNameChange = (text: string) => {
    setNameInput(text)
    // Sync into formik so validation and submit keep working unchanged.
    setFieldValue('name', text)
  }

  useEffect(() => {
    const fetchBreeds = async (): Promise<void> => {
      try {
        if (!selectedPet?.petType?.guid) return
        const res = await getPetBreedByType({ petTypeGUID: selectedPet.petType.guid })
        if (res.success) {
          setPetBreeds(res.data.petBreeds)
        }
      } catch (e) {
        // silent
      }
    }
    fetchBreeds()
  }, [selectedPet?.petType?.guid])

  useEffect(() => {
    const fetchBrachys = async (): Promise<void> => {
      try {
        const list = await getBrachycephalicBreeds()
        setBrachycephalicBreeds(list)
      } catch (e) {
        // silent
      }
    }
    fetchBrachys()
  }, [])

  const selectedBreedObj = useMemo(
    () => petBreeds.find((b) => b.guid === values.petBreed) ?? null,
    [petBreeds, values.petBreed],
  )

  useEffect(() => {
    const fetchRestriction = async (): Promise<void> => {
      if (!selectedBreedObj?.name) {
        setBreedRestriction(null)
        return
      }
      try {
        const res = await countCountriesWithBreedRestriction(selectedBreedObj.name)
        setBreedRestriction(res)
      } catch (e) {
        setBreedRestriction(null)
      }
    }
    fetchRestriction()
  }, [selectedBreedObj?.name])

  const isSelectedBreedBrachy = selectedBreedObj?.name
    ? matchesAnyBreed(selectedBreedObj.name, brachycephalicBreeds)
    : false

  const isCat = selectedPet?.petType?.name === 'Gato'
  const maxWeight = isCat ? 15 : 120
  const weightOptions = Array.from({ length: maxWeight }, (_, i) => ({ id: (i + 1).toString(), title: `${i + 1} kg`, data: i + 1 }))


  const onGalleryPress = (file: File, preview: string, type: ImageTypeSelector) => {
    setFieldValue('newAvatar', file)
    dispatch(loadSelectedPetAvatar(preview))
  }


  const handleConfirmationModal = () => {
    setShowConfirmationModal(!showConfirmationModal)
  }

  const handleDelete = () => {
    setShowConfirmationModal(false)
    deletePet().then(() => {
      setTimeout(() => navigate('/aena/home'), 100)

    })
  }

  console.log('values', values)

  return (
    <AppBackground isSafe>
      <View style={{ maxWidth: 800, width: '100%', alignSelf: 'center' }}>
        <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', padding: 16, gap: 16 }}>
          <ResponsiveLogo
            breakpoint={820}
            hysteresis={20}
            large={{ width: 680, height: 96 }}
            small={{ width: 360, height: 48 }}
          />
        </View>
        <HeaderWithBackAena title={t('common.editpet')} />
        <BottomActionContainer>
          <InputWrapper>
            <Headline5>{t('common.photo')} *</Headline5>
            <InputContainer>
              <CamaraCard
                title={t('common.choosephoto')}
                subtitle={t('common.upload_pet_photo')}
                onGalleryPress={onGalleryPress}
                url={avatar}
                type={ImageTypeSelector.EDIT_PET}
              />
            </InputContainer>

          </InputWrapper>
          <>            

          
            <InputWrapper>
              <Headline5>{t('common.name')}</Headline5>
              <InputContainer>
                <Input
                  key="pet-name-input"
                  inputProps={{
                    value: nameInput,
                    onChangeText: onNameChange,
                    onBlur: handleBlur('name'),
                    placeholder: t('common.name'),
                  }}
                />
                {errors.name && (
                  <ErrorText>
                    {errors.name?.toString()}
                  </ErrorText>
                )}
              </InputContainer>
            </InputWrapper>
            <InputWrapper>
              <Headline5>Smart Pet Tag</Headline5>
              <InputContainer>
                <Input
                  inputProps={{
                    value: values.locatorActivationCode,
                    onChangeText: handleChange('locatorActivationCode'),
                    onBlur: handleBlur('locatorActivationCode'),
                    placeholder: t('common.addsmartpettag'),
                  }}
                />
                {errors.locatorActivationCode && (
                  <ErrorText>
                    {errors.locatorActivationCode?.toString()}
                  </ErrorText>
                )}
              </InputContainer>
            </InputWrapper>


            <View style={{ flexDirection: 'row', gap: 16 }}>
              <View style={{ flex: 1 }}>
                <InputWrapper>
                <Headline5>{t('common.weigth')} *</Headline5>
                <InputContainer>
                  <Drop
                    data={weightOptions}
                    active={weightOptions.findIndex((obj) => obj.data === values.weight)}
                    selectedItem={weightOptions.findIndex((obj) => obj.data === values.weight)}
                    onPress={(item) => { setFieldValue('weight', item.data); handleBlur('weight') }}
                    placeholder={'0 kg'}
                    icon={<ChevronDownIcon width={22} height={22} />}
                    activeColor="#96ce00"
                    color="aena"
                    buttonText={t('common.continue')}
                    variant="outlined"
                  />
                </InputContainer>
                </InputWrapper>
              </View>
              <View style={{ flex: 1 }}>
              <InputWrapper>

              <Headline5>{t('common.breed')} *</Headline5>
              <InputContainer>
                <Drop
                  data={petBreeds.map((breed) => ({ id: breed.guid, title: breed.name, data: breed }))}
                  active={petBreeds.findIndex((b) => b.guid === values.petBreed)}
                  selectedItem={petBreeds.findIndex((b) => b.guid === values.petBreed)}
                  onPress={(item) => { setFieldValue('petBreed', item.id); handleBlur('petBreed') }}
                  placeholder={t('common.selectabreed')}
                  icon={<ChevronDownIcon width={22} height={22} />}
                  activeColor="#96ce00"
                  variant="outlined"
                  buttonText={t('common.continue')}
                  color="aena"
                />
                <BreedNotices
                  isBrachy={isSelectedBreedBrachy}
                  restriction={breedRestriction}
                  onPressTravelDocs={() => navigate('/aena/travel-docs')}
                />
              </InputContainer>
              </InputWrapper>
              </View>


            </View>
            <View style={{ flexDirection: 'row', gap: 16 }}>
              <View style={{ flex: 1 }}>
              <Headline5>{t('common.sterilized')} *</Headline5>
              <InputContainer>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <CheckBoxCustom
                      isChecked={ values.isSterilised === true }
                      onToggle={() => { setFieldValue('isSterilised', true); handleBlur('isSterilised') }}
                    />
                    <BodyText>{t('common.yes')}</BodyText>  
                  </View>
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <CheckBoxCustom
                      isChecked={ values.isSterilised === false }
                      onToggle={() => { setFieldValue('isSterilised', false); handleBlur('isSterilised') }}
                    />
                    <BodyText>{t('common.no')}</BodyText>
                  </View>
                </View>
              </InputContainer>       
              </View>
              <View style={{ flex: 1 }}>
                <Headline5>{t('common.sex')} *</Headline5>
                <InputContainer>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <CheckBoxCustom
                        isChecked={values.gender === 'MALE'}
                        onToggle={() => { setFieldValue('gender', 'MALE'); handleBlur('gender') }}
                      />
                      <BodyText>{t('common.male')}</BodyText>
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <CheckBoxCustom
                        isChecked={values.gender === 'FEMALE'}
                        onToggle={() => { setFieldValue('gender', 'FEMALE'); handleBlur('gender') }}
                      />
                      <BodyText>{t('common.female')}</BodyText>
                    </View>
                  </View>
                </InputContainer>
            </View>                     
            </View>


          </>            
          <Button
              title={t('common.update')}
              buttonWrapperStyle={{ backgroundColor: '#96ce00' }}
              size="block"
              textType="cta_medium"
              borderRadius={5}
              textColor="background0"
              color="primary"
              onPress={handleSubmit}
            />
        </BottomActionContainer>
        <div style={{ padding: 15 }}>
          <Button
            title={t('common.delete')}
            buttonWrapperStyle={{ backgroundColor: '#ce1f00ff' }}
            size="block"
            textType="cta_medium"
            borderRadius={5}
            textColor="background0"
            color="error"
            onPress={handleConfirmationModal}
          />
        </div>

        <Modal
          isVisible={showConfirmationModal}
          backdropOpacity={0.7}
          useNativeDriver
          useNativeDriverForBackdrop
          hideModalContentWhileAnimating
          onBackdropPress={() => setShowConfirmationModal(!showConfirmationModal)}>
          <ModalContainer>
            <ModalHeader>
              <Text type="H4" textAlign="center">
                {t('common.areyousureyouwanttodeletename', { name })}
              </Text>
            </ModalHeader>
            <Text type="H5" textAlign="center">
              {t('common.onceyoudeletenameyoucannotrecovertheinformation', { name })}
            </Text>
            <ButtonContainer>
              <Button
                title={t('common.cancel')}
                buttonWrapperStyle={{ backgroundColor: '#96ce00' }}
                size="small"
                textType="cta_medium"
                borderRadius={5}
                textColor="background0"
                color="primary"
                onPress={handleConfirmationModal}
              />
              <Button
                title={t('common.delete')}
                buttonWrapperStyle={{ backgroundColor: '#ce1f00ff' }}
                size="small"
                textType="cta_medium"
                borderRadius={5}
                textColor="background0"
                color="error"
                onPress={handleDelete}
              />
            </ButtonContainer>
          </ModalContainer>
        </Modal>
      </View>

    </AppBackground>
  )
}
